import React, { useLayoutEffect } from 'react';
import { Router } from 'react-router';
import { useDispatch } from 'react-redux';
import { AnyAction, Middleware, Reducer } from 'redux';
import { Action, History, Location, LocationDescriptor, LocationState } from 'history';

const LOCATION_CHANGE = '@@router/LOCATION_CHANGE';
const CALL_HISTORY_METHOD = '@@router/CALL_HISTORY_METHOD';

export interface RouterState<S = LocationState> {
  location: Location<S>;
  action: Action;
}

type HistoryMethod = 'push' | 'replace' | 'go' | 'goBack' | 'goForward';

const callHistoryMethod = (method: HistoryMethod, ...args: unknown[]): AnyAction => ({
  type: CALL_HISTORY_METHOD,
  payload: { method, args },
});

export const push = (path: LocationDescriptor, state?: LocationState) => callHistoryMethod('push', path, state);
export const replace = (path: LocationDescriptor, state?: LocationState) => callHistoryMethod('replace', path, state);
export const go = (amount: number) => callHistoryMethod('go', amount);
export const goBack = () => callHistoryMethod('goBack');
export const goForward = () => callHistoryMethod('goForward');

const locationChange = (location: Location, action: Action): AnyAction => ({
  type: LOCATION_CHANGE,
  payload: { location, action },
});

export const connectRouter = (history: History): Reducer<RouterState> => {
  const initialState = { location: history.location, action: history.action };
  return (state = initialState, action) => action.type === LOCATION_CHANGE ? action.payload as RouterState : state;
};

export const routerMiddleware = (history: History): Middleware => () => next => action => {
  if ((action as AnyAction).type !== CALL_HISTORY_METHOD) return next(action);
  const { method, args } = (action as AnyAction).payload as { method: HistoryMethod; args: unknown[] };
  return (history[method] as (...values: unknown[]) => unknown)(...args);
};

export function ConnectedRouter({ history, children }: { history: History; children?: React.ReactNode }) {
  const dispatch = useDispatch();

  useLayoutEffect(() => {
    dispatch(locationChange(history.location, history.action));
    return history.listen((location, action) => dispatch(locationChange(location, action)));
  }, [dispatch, history]);

  return <Router history={history}>{children}</Router>;
}
